'use client';

import React from 'react';

import { Button, ButtonProps } from './button';
import { Box } from './box';

export interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  icon: React.ReactNode;
  'aria-label': string;
  /**
   * Whether to enforce minimum 44x44px touch target for accessibility
   * @default true
   */
  touchFriendly?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, size = 'md', touchFriendly = true, ...props }, ref) => {
    // Visual sizes for the icon button
    const visualSizes = {
      xs: 24, // 1.5rem
      sm: 32, // 2rem
      md: 40, // 2.5rem
      lg: 48, // 3rem
      xl: 56, // 3.5rem
    };

    const visualSize = visualSizes[size as keyof typeof visualSizes] || visualSizes.md;
    const minTouchSize = 44; // Minimum touch target size in pixels

    // Calculate if we need to add invisible padding for touch target
    const needsTouchPadding = touchFriendly && visualSize < minTouchSize;
    const touchPadding = needsTouchPadding ? (minTouchSize - visualSize) / 2 : 0;

    if (needsTouchPadding) {
      // Wrap button with invisible touch target area
      return (
        <Box as="span" display="inline-block" position="relative" padding={`${touchPadding}px`}>
          <Button ref={ref} size={`icon-${size}` as ButtonProps['size']} {...props}>
            {icon}
          </Button>
          {/* Invisible touch target overlay */}
          <Box as="span" position="absolute" inset={0} pointerEvents="none" aria-hidden="true" />
        </Box>
      );
    }

    // For larger buttons, use standard implementation
    return (
      <Button ref={ref} size={`icon-${size}` as ButtonProps['size']} {...props}>
        {icon}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';
